/**
 * Precios en góndola, dentro del cierre de turno (1-nov).
 *
 * Bimbo (Franco Sarmiento, correo «RECOLECCIÓN DE INFORMACIÓN DE CAMPO» del 27-oct) pidió que el impulso levante en cada
 * tienda Categoría, Marca, SKU, Producto y PVP de siete categorías, **de todas las marcas** (su ejemplo es Unión), «para
 * tener el mapeo de todas las cadenas a las que tenemos acceso».
 *
 * Decisión del usuario del 1-nov: uno por uno, con lo que la impulsadora recuerde (en varias tiendas les quitan el celular
 * al entrar y lo llena de memoria), sugiriendo los productos Bimbo que conocemos; lo de otras marcas se escribe. **No es
 * obligatorio**: un precio inventado para poder enviar el cierre es peor que no tenerlo.
 *
 * El servidor (`guardar_precios_gondola`, 0367) vuelve a validar y descarta la línea que no sirve sin tumbar el cierre.
 */

/** Las siete del correo, con sus nombres. */
export const CATEGORIAS_DE_PRECIO = [
  'Pan Blanco', 'Pan Especial', 'Tortillas', 'Pita', 'Tostadas', 'Molido', 'Bollería',
] as const;
export type CategoriaDePrecio = (typeof CATEGORIAS_DE_PRECIO)[number];

/** Un producto anotado. `pvp` va como lo escribió ella («13,5»); se convierte al enviar. */
export interface PrecioGondola {
  categoria: string;
  marca: string;
  sku: string;
  producto: string;
  pvp: string;
  /** Lo eligió de la lista de Bimbo en vez de escribirlo. */
  sugerido: boolean;
}

/**
 * Los productos Bimbo que conocemos de Asymmetrika (las descargas de Vital, fijas y la lámina), con el nombre que usan
 * las impulsadoras. De Molido y Bollería no tenemos ninguno descargado: se escriben a mano, como la competencia.
 */
export const PRODUCTOS_BIMBO: readonly { categoria: CategoriaDePrecio; sku: string }[] = [
  { categoria: 'Pan Blanco', sku: 'Molde Blanco 770 g' },
  { categoria: 'Pan Blanco', sku: 'Blanco Grande 590 g' },
  { categoria: 'Pan Blanco', sku: 'Blanco Cero Cero 530 g' },
  { categoria: 'Pan Especial', sku: 'Vital Multicereal 360 g' },
  { categoria: 'Pan Especial', sku: 'Vital Multicereal 600 g' },
  { categoria: 'Pan Especial', sku: 'Vital Semillas 360 g' },
  { categoria: 'Pan Especial', sku: 'Vital Semillas 600 g' },
  { categoria: 'Pan Especial', sku: 'Vital Mix de Quinuas 600 g' },
  { categoria: 'Pan Especial', sku: 'Vital Fruticereal 360 g' },
  { categoria: 'Pan Especial', sku: 'Integral Cero Cero 650 g' },
  { categoria: 'Pita', sku: 'Pita Cero Cero 8 un. 240 g' },
  { categoria: 'Pita', sku: 'Pita Vital Semillas 6 un. 230 g' },
  { categoria: 'Pita', sku: 'Arabitas Cero Cero 10 un. 270 g' },
  { categoria: 'Tortillas', sku: 'Rapiditas Clásicas 12 un. 310 g' },
  { categoria: 'Tortillas', sku: 'Rapiditas Integrales 12 un. 310 g' },
  { categoria: 'Tortillas', sku: 'Rapiditas Linaza y Chía 10 un. 260 g' },
  { categoria: 'Tortillas', sku: 'Rapiditas Cero Cero 10 un. 280 g' },
  { categoria: 'Tortillas', sku: 'Rapiditas Wraps XL 6 un. 310 g' },
  { categoria: 'Tostadas', sku: 'Tostadas Clásicas 10 un. 160 g' },
  { categoria: 'Tostadas', sku: 'Tostadas Integrales 10 un. 180 g' },
  { categoria: 'Tostadas', sku: 'Tostadas Integrales Cero Cero 10 un. 140 g' },
];

/**
 * Quién lo ve: los cierres de Bimbo menos Takis (snacks: no está en las siete categorías, y su cierre lo lleva otra
 * sesión). Por cliente y marca, como el resto del cuestionario.
 */
export function pideBarrido(cliente: string | null | undefined, marca: string | null | undefined): boolean {
  return /bimbo/i.test(cliente ?? '') && !/takis/i.test(marca ?? '');
}

/** El producto como lo escribe el cliente en su ejemplo: «Pan Especial Unión Semilla 500gr». */
export function productoDe(categoria: string, marca: string, sku: string): string {
  return [categoria, marca, sku].map((x) => x.trim()).filter(Boolean).join(' ');
}

/** «13,5», «13.50» o «S/ 13.5» → 13.5. `null` si no es un precio. */
export function leerPvp(texto: string): number | null {
  const limpio = texto.replace(/s\/\.?/i, '').replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(limpio)) return null;
  const n = Number(limpio);
  return n > 0 && n < 1000 ? n : null;
}

/** Qué le falta a una línea antes de agregarla, en palabras para ella. `null` = se puede agregar. */
export function faltaEnPrecio(p: Pick<PrecioGondola, 'categoria' | 'marca' | 'sku' | 'pvp'>): string | null {
  if (!p.categoria.trim()) return 'Elige la categoría.';
  if (!p.marca.trim()) return 'Escribe la marca.';
  if (!p.sku.trim()) return 'Escribe el producto y su presentación (por ejemplo, Semilla 500 g).';
  if (leerPvp(p.pvp) === null) return 'Escribe el precio en soles (por ejemplo, 13,50).';
  return null;
}

/** Lo que viaja al servidor: el precio ya como número con punto. */
export function preciosParaEnviar(lista: PrecioGondola[]): { categoria: string; marca: string; sku: string; producto: string; pvp: string; sugerido: boolean }[] {
  return lista
    .map((p) => ({ ...p, pvp: String(leerPvp(p.pvp) ?? '') }))
    .filter((p) => p.pvp !== '');
}
